// Deterministic models of testing and debugging for Lesson 2.13 ("Prove It Works and Find
// What Broke"). They apply the lesson's tests, fixtures, dependency overrides and paging bug to
// the shared dataset; they do not run pytest, open connections or send requests.

import { COOKIE } from './session-models';

export type Tone = 'neutral' | 'success' | 'warning';
const quoted = (labels: string[]) => {
  const parts = labels.map((label) => `"${label}"`);
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}` : parts.join('');
};

// --- test-catch: which test catches which mistake ------------------------------------------

export type Mistake = 'paging' | 'member-delete' | 'visibility' | 'cookie';
export const MISTAKES: readonly Mistake[] = ['paging', 'member-delete', 'visibility', 'cookie'];
export const mistakeLabels: Record<Mistake, string> = {
  paging: 'Paging uses >=',
  'member-delete': 'A member may delete',
  visibility: 'Visible tasks ignore membership',
  cookie: 'The cookie loses Secure',
};
/** The change made on purpose to see which tests fail. */
export const mistakeChanges: Record<Mistake, string> = {
  paging: 'repository.project_tasks_after: t.id > %s became t.id >= %s',
  'member-delete': 'permissions.ACTIONS["delete_task"] became {"manager", "member"}',
  visibility: 'repository.VISIBLE: a project task shows to every signed-in person, member or not',
  cookie: 'routes.py: set_cookie(..., secure=True) became secure=False',
};

export type TestId =
  'unit-table' | 'db-visibility' | 'api-matrix' | 'api-paging' | 'api-cookie' | 'it-ran';
export interface LabTest {
  id: TestId;
  label: string;
  file: 'test_permissions.py' | 'test_repository.py' | 'test_api.py';
  /** The mistakes that make this test fail. */
  catches: readonly Mistake[];
  /** What the test sees with each mistake in place. */
  why: Record<Mistake, string>;
}
const NO_COOKIE = 'client_as overrides current_user, so its requests carry no cookie.';
const LOGIN_ONLY = 'It logs in and reads set-cookie;';
export const LAB_TESTS: readonly LabTest[] = [
  {
    id: 'unit-table',
    label: 'Unit: the permission table',
    file: 'test_permissions.py',
    catches: ['member-delete'],
    why: {
      paging: 'allowed() runs no SQL, so no page is ever loaded.',
      'member-delete':
        'allowed("member", "delete_task") now returns True; the table expects False.',
      visibility: 'The table is still right. The mistake is in a query, and a unit test runs none.',
      cookie: 'A unit test sends no HTTP, so no cookie is set.',
    },
  },
  {
    id: 'db-visibility',
    label: 'Database: who sees which task',
    file: 'test_repository.py',
    catches: ['visibility'],
    why: {
      paging: 'It lists the tasks a person may see; it never asks for a second page.',
      'member-delete': 'Who may delete is a rule in permissions.py, not in the queries it runs.',
      visibility:
        "A person outside the project now gets the project's tasks; the test expects only their own.",
      cookie: 'It talks to PostgreSQL directly: no HTTP, no cookie.',
    },
  },
  {
    id: 'api-matrix',
    label: 'API: the permission matrix',
    file: 'test_api.py',
    catches: ['member-delete', 'visibility'],
    why: {
      paging: 'Every status is still right; none of its requests asks for a second page.',
      'member-delete': 'The member deletes a task: expected 403, got 204.',
      visibility: 'The outsider reads a project task: expected 404, got 200.',
      cookie: NO_COOKIE,
    },
  },
  {
    id: 'api-paging',
    label: 'API: pages never repeat a task',
    file: 'test_api.py',
    catches: ['paging'],
    why: {
      paging: 'Page 2 starts with the last task of page 1: the same ID is on both pages.',
      'member-delete': 'It only reads pages; it never deletes.',
      visibility: 'It pages as a member, who may see every task in the project anyway.',
      cookie: NO_COOKIE,
    },
  },
  {
    id: 'api-cookie',
    label: 'API: the login cookie',
    file: 'test_api.py',
    catches: ['cookie'],
    why: {
      paging: `${LOGIN_ONLY} it never pages.`,
      'member-delete': `${LOGIN_ONLY} it never deletes.`,
      visibility: `${LOGIN_ONLY} it never lists tasks.`,
      cookie: `It logs in for real and finds no Secure in set-cookie. A browser refuses the ${COOKIE} cookie without it.`,
    },
  },
  {
    id: 'it-ran',
    label: 'It ran: GET /tasks answers 200',
    file: 'test_api.py',
    catches: [],
    why: {
      paging: 'GET /tasks does not page, and the test checks only the status.',
      'member-delete': 'It never tries a delete, and GET /tasks still answers 200.',
      visibility: 'GET /tasks still answers 200. The rows are wrong, but it never reads the rows.',
      cookie: 'It never reads set-cookie, and GET /tasks still answers 200.',
    },
  },
];
/** The whole of the it-ran test: it calls the route and checks only the status. */
export const IT_RAN_TEST = [
  'response = client.get("/tasks")',
  'assert response.status_code == 200',
].join('\n');

export type Outcome = 'Caught' | 'Missed';
export const outcome = (test: LabTest, mistake: Mistake): Outcome =>
  test.catches.includes(mistake) ? 'Caught' : 'Missed';
export const catchingTests = (mistake: Mistake) =>
  LAB_TESTS.filter((test) => outcome(test, mistake) === 'Caught');
export const catchRows = (mistake: Mistake): string[][] =>
  LAB_TESTS.map((test) => [test.label, test.file, outcome(test, mistake), test.why[mistake]]);

const catchSummary: Record<Mistake, string> = {
  paging: 'Only a test that asks for a second page reaches the paging SQL.',
  'member-delete':
    'The unit test checks the table itself, and the API matrix sends the delete as a member and expects 403.',
  visibility:
    'The rule lives in a query, so only tests that run the query see the extra rows: the database test directly and the API matrix through HTTP.',
  cookie:
    'client_as skips sign-in, so only the one test that still logs in for real sees set-cookie.',
};
const itRanPasses: Record<Mistake, string> = {
  paging: 'GET /tasks does not page, and the test checks only the status.',
  'member-delete': 'it never tries a delete.',
  visibility: 'the status is still 200. Only the rows are wrong, and the test never reads them.',
  cookie: 'it never reads set-cookie.',
};
export function catchMessage(mistake: Mistake) {
  const failing = catchingTests(mistake);
  const count = `${failing.length} of ${LAB_TESTS.length} tests ${failing.length === 1 ? 'fails' : 'fail'}`;
  const itRan = LAB_TESTS.find((test) => test.id === 'it-ran')?.label ?? '';
  return `${count}: ${quoted(failing.map((test) => test.label))}. ${catchSummary[mistake]} "${itRan}" passes anyway: ${itRanPasses[mistake]}`;
}

// --- fixture-rollback: do tests depend on each other ---------------------------------------

export type Fixture = 'shared' | 'rollback';
export type RunOrder = 'ab' | 'ba';
export const FIXTURES: readonly Fixture[] = ['shared', 'rollback'];
export const RUN_ORDERS: readonly RunOrder[] = ['ab', 'ba'];
export const fixtureLabels: Record<Fixture, string> = {
  shared: 'Shared database',
  rollback: 'Rolled-back transaction',
};
export const orderLabels: Record<RunOrder, string> = { ab: 'A then B', ba: 'B then A' };
export interface FixtureTask {
  id: number;
  title: string;
}
/** The project's two tasks when the run starts. */
export const STARTING_TASKS: readonly FixtureTask[] = [
  { id: 20, title: 'First task' },
  { id: 21, title: 'Second task' },
];
export const DRAFT: FixtureTask = { id: 22, title: 'Draft' };
export type TestName = 'A' | 'B';
export interface FixtureTestSpec {
  label: string;
  expected: number;
  writesDraft: boolean;
}
export const fixtureTests: Record<TestName, FixtureTestSpec> = {
  A: {
    label: "Test A: creates task 'Draft' and expects 3 tasks in the project",
    expected: 3,
    writesDraft: true,
  },
  B: { label: 'Test B: expects 2 tasks in the project', expected: 2, writesDraft: false },
};
export const fixtureCode: Record<Fixture, string> = {
  shared: [
    '@pytest.fixture',
    'def connection():',
    '    with connect() as connection:',
    '        yield connection  # the block commits when the test ends',
  ].join('\n'),
  rollback: [
    '@pytest.fixture',
    'def connection():',
    '    connection = connect()',
    '    yield connection',
    '    connection.rollback()  # undo everything the test wrote',
    '    connection.close()',
  ].join('\n'),
};
export interface FixtureTestRun {
  test: TestName;
  label: string;
  /** The project's tasks when the test counts them, with where each row came from. */
  sees: [string, string, string][];
  count: number;
  expected: number;
  passed: boolean;
  verdict: string;
}
export interface FixtureRun {
  tests: FixtureTestRun[];
  after: FixtureTask[];
  afterLine: string;
  message: string;
  tone: Tone;
}
const runOrder = (order: RunOrder): TestName[] => (order === 'ab' ? ['A', 'B'] : ['B', 'A']);
export const idLine = (tasks: readonly FixtureTask[]) => tasks.map((task) => task.id).join(', ');
const fixtureMessages: Record<Fixture, Record<RunOrder, string>> = {
  shared: {
    ab: "Test A passes and Test B fails. A's Draft was committed to the shared database, so B counted 3 tasks where it expects 2. B is not wrong: it failed because of a test that ran before it. Draft is still in the table after the run, so running the tests again fails A too.",
    ba: 'Both tests pass, but only because B ran before A wrote Draft. In the other order B fails: the tests depend on each other. Draft is still in the table after the run, so the next run starts from different data.',
  },
  rollback: {
    ab: "Both tests pass. A's Draft existed only inside A's transaction, and the fixture rolled it back before B started, so B counted the 2 starting tasks. The table after the run is the same as before.",
    ba: 'Both tests pass in this order too. Each test sees the 2 starting tasks plus only what it wrote itself, and the rollback leaves the table as it was.',
  },
};
export function fixtureRun(fixture: Fixture, order: RunOrder): FixtureRun {
  let table: FixtureTask[] = [...STARTING_TASKS];
  const tests = runOrder(order).map((test): FixtureTestRun => {
    const spec = fixtureTests[test];
    const writes = spec.writesDraft ? [DRAFT] : [];
    if (fixture === 'shared') table = [...table, ...writes];
    const visible = fixture === 'shared' ? table : [...table, ...writes];
    const sees = visible.map((task): [string, string, string] => {
      let from = 'starting row';
      if (task.id === DRAFT.id)
        from = spec.writesDraft
          ? fixture === 'shared'
            ? 'written by Test A, committed'
            : 'written by Test A, inside its transaction'
          : 'left behind by Test A';
      return [String(task.id), task.title, from];
    });
    const count = visible.length;
    const passed = count === spec.expected;
    return {
      test,
      label: spec.label,
      sees,
      count,
      expected: spec.expected,
      passed,
      verdict: `${passed ? 'PASSED' : 'FAILED'}: ${count} tasks, expected ${spec.expected}`,
    };
  });
  const changed = table.length !== STARTING_TASKS.length;
  return {
    tests,
    after: table,
    afterLine: `${idLine(table)} (${changed ? 'Draft stays' : 'same as before'})`,
    message: fixtureMessages[fixture][order],
    tone: fixture === 'shared' ? 'warning' : 'success',
  };
}
export const FIXTURE_PROMPT =
  'Predict whether each test passes with this fixture and order, then run the tests.';

// --- override-flow: what the route receives in a test --------------------------------------

export const RAVI_USER = '{"id": 2, "name": "Ravi", "username": "ravi"}';
export const LOGIN_REQUEST =
  'client.post("/auth/login", json={"username": "ravi", "password": "summer2026"})';
export const CREATE_REQUEST = 'POST /projects/1/tasks';
export function overrideCode(connection: boolean, user: boolean) {
  return [
    'client = TestClient(app, base_url="https://testserver")',
    ...(connection || user
      ? []
      : ["# No overrides: the app's own get_connection and current_user run."]),
    ...(connection
      ? [
          'def use_test_connection():',
          "    yield connection  # the fixture's connection, rolled back after the test",
          'app.dependency_overrides[get_connection] = use_test_connection',
        ]
      : []),
    ...(user ? [`app.dependency_overrides[current_user] = lambda: ${RAVI_USER}`] : [LOGIN_REQUEST]),
    'response = client.post("/projects/1/tasks", json={"title": "Draft"})',
    'assert response.status_code == 201',
  ].join('\n');
}
export const PATH_PARTS = [
  'TestClient',
  'current_user',
  'route',
  'services',
  'repository',
  'PostgreSQL',
] as const;
export interface OverrideStep {
  part: (typeof PATH_PARTS)[number];
  /** Short tag: what this part is in the current setup. */
  tag: string;
  overridden: boolean;
  detail: string;
}
export function overridePath(connection: boolean, user: boolean): OverrideStep[] {
  const where = connection ? "the test's connection" : 'a new connection from get_connection';
  return [
    {
      part: 'TestClient',
      tag: user ? 'no login' : 'logs in first',
      overridden: false,
      detail: user
        ? `Sends ${CREATE_REQUEST} with no cookie.`
        : `Logs in as ravi with POST /auth/login, keeps the ${COOKIE} cookie, then sends ${CREATE_REQUEST} with it.`,
    },
    {
      part: 'current_user',
      tag: user ? 'overridden' : 'real',
      overridden: user,
      detail: user
        ? `Overridden: returns ${RAVI_USER} without reading a cookie.`
        : `Reads the ${COOKIE} cookie and finds Ravi's session row ${connection ? "inside the test's transaction" : 'that the login committed'}.`,
    },
    {
      part: 'route',
      tag: connection ? 'test connection' : 'real connection',
      overridden: connection,
      detail: `Receives ${where} and Ravi ${user ? 'from the override' : 'from his session'}.`,
    },
    {
      part: 'services',
      tag: 'unchanged',
      overridden: false,
      detail:
        'create_project_task checks that Ravi is a member of Apollo, who may create a task, on the connection it was given.',
    },
    {
      part: 'repository',
      tag: 'unchanged',
      overridden: false,
      detail: 'insert_project_task runs INSERT INTO tasks on the same connection.',
    },
    {
      part: 'PostgreSQL',
      tag: connection ? 'rolled back' : 'committed',
      overridden: connection,
      detail: connection
        ? "The INSERT stays inside the test's transaction; the fixture rolls it back after the test."
        : 'get_connection commits when the route returns: Draft is saved for good.',
    },
  ];
}
export interface OverrideOutcome {
  receives: string;
  needsLogin: string;
  afterTest: string;
  response: string;
  message: string;
  tone: Tone;
}
const overrideMessages = {
  none: 'Without overrides the test is a real client. It must log in as Ravi first, so a bug in sessions fails it, and get_connection commits every write: Draft and the new session row stay in the database after the test.',
  connection:
    "The route and current_user now use the test's connection, so Draft and the session row roll back with it. The test still logs in first, so it still fails whenever sign-in breaks.",
  user: 'current_user returns Ravi without reading a cookie, so no login is needed. But get_connection still opens its own connection and commits: Draft stays in the database after the test.',
  both: "The route receives the test's connection and Ravi from the override: no login, no cookie, and the fixture rolls back everything the request wrote. One separate test still logs in for real to check sign-in and the cookie.",
};
export function overrideOutcome(connection: boolean, user: boolean): OverrideOutcome {
  const key = connection ? (user ? 'both' : 'connection') : user ? 'user' : 'none';
  return {
    receives: `${connection ? "The test's connection" : 'A new connection from get_connection'} and Ravi ${user ? 'from the override' : 'from his session'}`,
    needsLogin: user ? 'No' : 'Yes: POST /auth/login first',
    afterTest: connection
      ? 'Everything rolled back: the database is as before'
      : user
        ? 'Draft stays in the database'
        : "Draft and Ravi's new session row stay in the database",
    response: '201 Created in every case',
    message: overrideMessages[key],
    tone: key === 'both' ? 'success' : 'warning',
  };
}

// --- debug-trace: where the duplicate comes from -------------------------------------------

export const PAGE_SIZE = 5;
export interface TraceTask {
  id: number;
  title: string;
}
/** Apollo's tasks in the debugging story (11 is Jo's personal task). */
export const LAUNCH_TASKS: readonly TraceTask[] = [
  { id: 9, title: 'Write the release note' },
  { id: 10, title: 'Check the dates' },
  { id: 12, title: 'Draft the agenda' },
  { id: 13, title: 'Send the invites' },
  { id: 14, title: 'Print the badges' },
  { id: 15, title: 'Confirm the venue' },
  { id: 16, title: 'Plan the seating' },
];
export type TraceStep = 'page' | 'network' | 'log' | 'sql';
export const TRACE_STEPS: readonly TraceStep[] = ['page', 'network', 'log', 'sql'];
export const traceStepLabels: Record<TraceStep, string> = {
  page: 'Page',
  network: 'Network tab',
  log: 'Server log',
  sql: 'SQL with the same values',
};
export type Hypothesis = 'page-twice' | 'api-boundary' | 'sql-gte';
export const HYPOTHESES: readonly Hypothesis[] = ['page-twice', 'api-boundary', 'sql-gte'];
export const hypothesisLabels: Record<Hypothesis, string> = {
  'page-twice': 'The page appends a page twice',
  'api-boundary': 'The API returns the boundary task',
  'sql-gte': 'The SQL uses >=',
};
/** The step whose evidence tests each hypothesis at its boundary. */
export const hypothesisCheck: Record<Hypothesis, TraceStep> = {
  'page-twice': 'network',
  'api-boundary': 'network',
  'sql-gte': 'sql',
};

export interface TracePage {
  after: number;
  request: string;
  sql: string;
  /** Rows PostgreSQL returns: up to PAGE_SIZE + 1. */
  fetched: TraceTask[];
  /** Tasks in the response. */
  tasks: TraceTask[];
  nextAfter: number | null;
  response: string;
}
export const pagingCondition = (fixed: boolean) => (fixed ? 't.id > %s' : 't.id >= %s');
export function tracePage(after: number, fixed: boolean): TracePage {
  const fetched = LAUNCH_TASKS.filter((task) => (fixed ? task.id > after : task.id >= after)).slice(
    0,
    PAGE_SIZE + 1,
  );
  const tasks = fetched.slice(0, PAGE_SIZE);
  const nextAfter = fetched.length > PAGE_SIZE ? (tasks.at(-1)?.id ?? null) : null;
  return {
    after,
    request: `GET /projects/1/tasks?after=${after}&limit=${PAGE_SIZE}`,
    sql: [
      'SELECT t.id, t.title FROM tasks AS t',
      `WHERE t.project_id = 1 AND t.id ${fixed ? '>' : '>='} ${after}`,
      `ORDER BY t.id LIMIT ${PAGE_SIZE + 1};`,
    ].join('\n'),
    fetched,
    tasks,
    nextAfter,
    response: `{"tasks": [${tasks.map((task) => `{"id": ${task.id}, …}`).join(', ')}], "next_after": ${nextAfter ?? 'null'}}`,
  };
}
export interface TraceRun {
  page1: TracePage;
  page2: TracePage;
  /** Every row on Maya's page after Load more tasks, in order. */
  shown: { task: TraceTask; page: 1 | 2 }[];
  repeated: number[];
}
export function traceRun(fixed: boolean): TraceRun {
  const page1 = tracePage(0, fixed);
  const page2 = tracePage(page1.nextAfter ?? 0, fixed);
  const shown = [
    ...page1.tasks.map((task) => ({ task, page: 1 as const })),
    ...page2.tasks.map((task) => ({ task, page: 2 as const })),
  ];
  const repeated = page1.tasks
    .filter((task) => page2.tasks.some((entry) => entry.id === task.id))
    .map((task) => task.id);
  return { page1, page2, shown, repeated };
}
export function pageRows(fixed: boolean): string[][] {
  const { shown, repeated } = traceRun(fixed);
  return shown.map((entry, index) => [
    String(index + 1),
    String(entry.task.id),
    entry.task.title,
    repeated.includes(entry.task.id) && entry.page === 2 ? 'page 2, again' : `page ${entry.page}`,
  ]);
}
const LOG_PREFIX = 'INFO:     127.0.0.1:52814 - ';
export function networkLines(fixed: boolean) {
  const { page1, page2 } = traceRun(fixed);
  return [page1, page2].flatMap((page, index) => [
    ...(index ? [''] : []),
    `${page.request}    200`,
    `Response: ${page.response}`,
  ]);
}
export function logLines(fixed: boolean) {
  const { page1, page2 } = traceRun(fixed);
  return [
    ...[page1, page2].map((page) => `${LOG_PREFIX}"${page.request} HTTP/1.1" 200 OK`),
    '',
    '(no traceback)',
  ];
}
/** psql's aligned output: headers centred, numbers right-aligned, text left-aligned. */
export function psqlTable(rows: readonly TraceTask[]) {
  const idWidth = Math.max(2, ...rows.map((task) => String(task.id).length));
  const titleWidth = Math.max(5, ...rows.map((task) => task.title.length));
  const centre = (text: string, width: number) =>
    `${' '.repeat(Math.floor((width - text.length) / 2))}${text}`;
  return [
    ` ${centre('id', idWidth).padEnd(idWidth)} | ${centre('title', titleWidth)}`,
    `${'-'.repeat(idWidth + 2)}+${'-'.repeat(titleWidth + 2)}`,
    ...rows.map((task) => ` ${String(task.id).padStart(idWidth)} | ${task.title}`),
    `(${rows.length} row${rows.length === 1 ? '' : 's'})`,
  ];
}
export function sqlLines(fixed: boolean) {
  const { page2 } = traceRun(fixed);
  return [page2.sql, '', ...psqlTable(page2.fetched)];
}
export const evidenceLines = (step: TraceStep, fixed: boolean) =>
  step === 'network' ? networkLines(fixed) : step === 'log' ? logLines(fixed) : sqlLines(fixed);

const evidence: Record<TraceStep, Record<'bug' | 'fixed', string>> = {
  page: {
    bug: 'Print the badges (14) appears twice: last on page 1 and first on page 2. The bug is reproduced, and every hypothesis still fits.',
    fixed: 'Every task appears once: page 2 starts with Confirm the venue (15).',
  },
  network: {
    bug: "Page 2's response itself starts with task 14, so the page shows exactly what the API sent. The duplicate comes from the server.",
    fixed: "Page 2's response starts with task 15.",
  },
  log: {
    bug: 'Both requests answered 200 with no traceback, and the second arrived with after=14, the last ID on page 1. The page sent the right value and nothing crashed: the server returned a wrong answer without an error.',
    fixed: 'Both requests answered 200, as before: the log looked normal even with the bug.',
  },
  sql: {
    bug: "With the request's values (project 1, after 14, limit 5 + 1) the query returns 14, 15, 16: t.id >= 14 keeps the task page 1 already showed.",
    fixed: 'With t.id > 14 the query returns 15, 16.',
  },
};
export const stepFinding = (step: TraceStep, fixed: boolean) =>
  evidence[step][fixed ? 'fixed' : 'bug'];

export type HypothesisStatus = 'Open' | 'Ruled out' | 'Confirmed' | 'Confirmed: caused by the SQL';
export function hypothesisStatus(
  hypothesis: Hypothesis,
  seen: readonly TraceStep[],
): HypothesisStatus {
  const network = seen.includes('network');
  const sql = seen.includes('sql');
  if (hypothesis === 'page-twice') return network ? 'Ruled out' : 'Open';
  if (hypothesis === 'api-boundary')
    return network ? (sql ? 'Confirmed: caused by the SQL' : 'Confirmed') : 'Open';
  return sql ? 'Confirmed' : 'Open';
}
export const hypothesisRows = (seen: readonly TraceStep[]): string[][] =>
  HYPOTHESES.map((hypothesis) => [
    hypothesisLabels[hypothesis],
    traceStepLabels[hypothesisCheck[hypothesis]],
    hypothesisStatus(hypothesis, seen),
  ]);
function hypothesisSentence(hypothesis: Hypothesis, seen: readonly TraceStep[]) {
  const label = `"${hypothesisLabels[hypothesis]}"`;
  const status = hypothesisStatus(hypothesis, seen);
  if (hypothesis === 'page-twice')
    return status === 'Ruled out'
      ? `${label} is ruled out: the Network tab shows page 2's response already contains task 14, so the page shows what the API sent.`
      : `${label} is still open: the Network tab, at the boundary between the page and the API, tests it.`;
  if (hypothesis === 'api-boundary') {
    if (status === 'Open')
      return `${label} is still open: the Network tab shows what the API sent.`;
    return status === 'Confirmed'
      ? `${label} is confirmed: page 2's response starts with task 14. Next, test the boundary between the API and the database: run the SQL with the same values.`
      : `${label} holds, but the SQL explains it: the query with the same values returns task 14 too, so the API passes on what the database returned.`;
  }
  return status === 'Confirmed'
    ? `${label} is confirmed: with after=14 the query returns 14, 15, 16.`
    : `${label} is still open: run the query with the request's values (project 1, after 14, limit 5 + 1) to test it.`;
}
export const TRACE_START =
  'Start with Page to reproduce what Maya saw, then follow the request through the Network tab, the server log and the SQL.';
export const CAUSE =
  'The duplicate starts in the repository: project_tasks_after pages with t.id >= %s. Fix: t.id > %s, with test_pages_never_repeat_a_task written first so it fails before the fix.';
export const FIXED =
  'Fixed: with t.id > %s page 2 is 15, 16 and every task appears once. Keep test_pages_never_repeat_a_task as a regression test: it failed before the fix and passes now.';
export function traceMessage(
  step: TraceStep | null,
  seen: readonly TraceStep[],
  hypothesis: Hypothesis,
  fixed: boolean,
) {
  if (fixed) return `${step ? `${stepFinding(step, true)} ` : ''}${FIXED}`;
  if (!step) return TRACE_START;
  const cause = seen.includes('sql') ? ` ${CAUSE}` : '';
  return `${stepFinding(step, false)} ${hypothesisSentence(hypothesis, seen)}${cause}`;
}
export function traceLayer(seen: readonly TraceStep[]) {
  if (seen.includes('sql')) return 'Repository: the SQL in project_tasks_after';
  if (seen.includes('network')) return "On the server: the API's response already has it";
  return 'Not known yet';
}
export const traceTone = (seen: readonly TraceStep[], fixed: boolean): Tone =>
  fixed ? 'success' : seen.includes('sql') ? 'warning' : 'neutral';
